<template>
  <div class="col-md-12 px-0">
    <div>
      <div class="card">
        <div class="card-header border-transparent bg-primary">
          <h3 class="card-title" v-html="formattedTitle"></h3>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table p-0">
              <thead>
                <tr class="text-center">
                  <th>{{ $t(dateHourLabel) }}</th>
                  <th>{{ $t('services/concierge_dashboard.fields.client') }}</th>
                  <th>{{ $t('services/concierge_dashboard.fields.type') }}</th>
                  <th>{{ $t('services/concierge_dashboard.fields.vehicles') }}</th>
                  <th>{{ $t('services/concierge_dashboard.fields.adviser') }}</th>
                  <th>{{ $t('services/concierge_dashboard.fields.action') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="appointments.length < 1">
                  <tr class="text-center">
                    <td colspan="6">{{ $t('services/concierge_dashboard.no_appointments') }}</td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="appointment in appointments" :key="appointment.id" class="text-center">
                    <td>
                      <template v-if="displayHour">
                        {{ appointment.formattedHour }}
                      </template>
                      <template v-else>
                        {{ appointment.formattedDate }}
                      </template>
                    </td>
                    <td>
                      <a :href="appointment.endpoints.customer">
                        {{ appointment.customer }}
                      </a>
                    </td>
                    <td>
                      <template v-if="!appointment.type"> {{ $t('Undefined') }} </template>
                      <template v-else>
                        {{ $t('enums/concierge_appointment_types.' + appointment.type) }}
                      </template>
                    </td>
                    <td>
                      <template v-if="displayVehicleLink && appointment.vehicle">
                        <a :href="appointment.endpoints.vehicle">
                          {{ appointment.vehicle }}
                        </a>
                      </template>
                      <template
                        v-else-if="!appointment.isTestDrive && appointment.vehicleNames.length < 1"
                      >
                        N/A
                      </template>
                      <small v-else>
                        {{ appointment.vehicleNames }}
                      </small>
                    </td>
                    <td>
                      <template v-if="!appointment.adviser"> N/A </template>
                      <template v-else>
                        <a :href="appointment.endpoints.adviser">
                          {{ appointment.adviser }}
                        </a>
                      </template>
                    </td>
                    <td>
                      <div class="btn-group">
                        <a
                          v-if="$can('edit concierge appointments')"
                          :href="appointment.endpoints.appointment"
                          class="btn btn-secondary btn-sm"
                          target="_blank"
                        >
                          <i class="fa fa-edit"></i>
                        </a>
                        <button
                          v-if="$can('delete concierge appointments')"
                          type="button"
                          class="btn btn-danger btn-sm"
                          @click="showModal(appointment)"
                        >
                          <i class="delete-icon fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
            <DeleteModal v-if="showDeleteModal" @hideModal="hideModal" class="text-left">
              <template #title>
                {{ $t('services/concierge_dashboard.delete_appointment') }}
              </template>
              <template #content>
                {{
                  $tChoice(
                    'services/concierge_dashboard.delete_appointment_client_confirmation',
                    selectedAppointment?.customerAppointmentsCount || 0
                  )
                }}
              </template>
              <template #footer>
                <div id="error" class="alert alert-danger col-md-12" v-if="errorMessage">
                  {{ errorMessage }}
                </div>
                <button type="button" class="btn btn-default bg-secondary" @click="hideModal">
                  {{ $t('crud.cancel') }}
                </button>
                <template v-if="$can('delete concierge appointments')">
                  <button
                    type="button"
                    class="btn btn-default bg-danger"
                    :disabled="form.processing"
                    @click="deleteRecord(selectedAppointment.id, false)"
                  >
                    {{ $t('services/concierge_dashboard.only_appointment') }}
                  </button>
                  <button
                    v-if="selectedAppointment.customerAppointmentsCount === 1"
                    type="button"
                    class="btn btn-default bg-danger"
                    :disabled="form.processing"
                    @click="deleteRecord(selectedAppointment.id, true)"
                  >
                    {{ $t('services/concierge_dashboard.appointment_and_client') }}
                  </button>
                </template>
              </template>
            </DeleteModal>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DeleteModal from '@components/modals/DeleteModal.vue'
import { defineComponent } from 'vue'
import { useForm } from '@inertiajs/vue3'

export default defineComponent({
  name: 'DashboardAppointments',
  setup() {
    const form = useForm({
      removeClient: false
    })
    return {
      form
    }
  },
  props: {
    appointments: {
      type: Array,
      required: true
    },
    titleKey: {
      type: String,
      required: true
    },
    displayHour: {
      type: Boolean,
      default: false
    },
    displayVehicleLink: {
      type: Boolean,
      default: false
    },
    dateHourLabel: {
      type: String,
      default: 'services/concierge_dashboard.fields.date_hour'
    }
  },
  components: { DeleteModal },
  data() {
    return {
      showDeleteModal: false,
      errorMessage: '',
      selectedAppointment: null
    }
  },
  methods: {
    deleteRecord(id, removeClient = false) {
      this.form.removeClient = removeClient
      this.form.delete(route('concierge.delete_appointment', { id: id }), {
        preserveScroll: true,
        onSuccess: () => {
          this.form.reset()
          this.showDeleteModal = false
        },
        onError: errors => {
          this.errorMessage = errors.message
        }
      })
    },
    hideModal() {
      this.showDeleteModal = false
    },
    showModal(appointment) {
      this.showDeleteModal = true
      this.selectedAppointment = appointment
    }
  },
  computed: {
    formattedTitle() {
      return this.$t(this.titleKey).replace('RDV', '<span style="font-weight: 600;">RDV</span>')
    }
  }
})
</script>
